import {
    ClientAccident,
    ClientCompanyFile,
    ClientDOTInspection,
    ClientDrugTestRecord,
    ClientIFTARecord,
    ComplianceAlert,
    DqfChecklistItemConfig,
    InspectionAttachmentSummary,
} from '@numeo/types-core'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from 'store'
import { SafetyDashboardData, SafetyDriver, SafetyTruck } from '../services/safetyApi'

export interface SafetyState {
    dashboard: { data: SafetyDashboardData | null; loading: boolean; error: string | null }
    drivers: { items: SafetyDriver[]; loading: boolean; error: string | null }
    trucks: { items: SafetyTruck[]; loading: boolean; error: string | null }
    dotInspections: { items: ClientDOTInspection[]; loading: boolean; error: string | null }
    accidents: { items: ClientAccident[]; loading: boolean; error: string | null }
    drugTests: { items: ClientDrugTestRecord[]; loading: boolean; error: string | null }
    iftaReports: { items: ClientIFTARecord[]; loading: boolean; error: string | null }
    companyFiles: { items: ClientCompanyFile[]; loading: boolean; error: string | null }
    complianceAlerts: { data: ComplianceAlert[]; loading: boolean; error: string | null }
    dqfChecklistConfig: { items: DqfChecklistItemConfig[]; loading: boolean; error: string | null }
}

const initialState: SafetyState = {
    dashboard: { data: null, loading: false, error: null },
    drivers: { items: [], loading: false, error: null },
    trucks: { items: [], loading: false, error: null },
    dotInspections: { items: [], loading: false, error: null },
    accidents: { items: [], loading: false, error: null },
    drugTests: { items: [], loading: false, error: null },
    iftaReports: { items: [], loading: false, error: null },
    companyFiles: { items: [], loading: false, error: null },
    complianceAlerts: { data: [], loading: false, error: null },
    dqfChecklistConfig: { items: [], loading: false, error: null },
}

const safetySlice = createSlice({
    name: 'safety',
    initialState,
    reducers: {
        // Dashboard
        setSafetyDashboardLoading(state, action: PayloadAction<boolean>) {
            state.dashboard.loading = action.payload
        },
        setSafetyDashboardData(state, action: PayloadAction<SafetyDashboardData>) {
            state.dashboard.data = action.payload
            state.dashboard.loading = false
            state.dashboard.error = null
        },
        setSafetyDashboardError(state, action: PayloadAction<string>) {
            state.dashboard.error = action.payload
            state.dashboard.loading = false
        },

        // Drivers
        setDriversLoading(state, action: PayloadAction<boolean>) {
            state.drivers.loading = action.payload
        },
        setDrivers(state, action: PayloadAction<SafetyDriver[]>) {
            state.drivers.items = action.payload
            state.drivers.loading = false
            state.drivers.error = null
        },
        setDriversError(state, action: PayloadAction<string>) {
            state.drivers.error = action.payload
            state.drivers.loading = false
        },

        // Trucks
        setTrucksLoading(state, action: PayloadAction<boolean>) {
            state.trucks.loading = action.payload
        },
        setTrucks(state, action: PayloadAction<SafetyTruck[]>) {
            state.trucks.items = action.payload
            state.trucks.loading = false
            state.trucks.error = null
        },
        setTrucksError(state, action: PayloadAction<string>) {
            state.trucks.error = action.payload
            state.trucks.loading = false
        },

        // DOT Inspections
        setDOTInspectionsLoading(state, action: PayloadAction<boolean>) {
            state.dotInspections.loading = action.payload
        },
        setDOTInspections(state, action: PayloadAction<ClientDOTInspection[]>) {
            state.dotInspections.items = action.payload
            state.dotInspections.loading = false
            state.dotInspections.error = null
        },
        setDOTInspectionsError(state, action: PayloadAction<string>) {
            state.dotInspections.error = action.payload
            state.dotInspections.loading = false
        },
        // An attachment upload/remove already returns the updated list, so patch that one inspection instead of refetching all of them.
        updateDOTInspectionAttachments(state, action: PayloadAction<{ id: string; attachments: InspectionAttachmentSummary[] }>) {
            const inspection = state.dotInspections.items.find((item) => item.id === action.payload.id)
            if (inspection) inspection.attachments = action.payload.attachments
        },

        // Accidents
        setAccidentsLoading(state, action: PayloadAction<boolean>) {
            state.accidents.loading = action.payload
        },
        setAccidents(state, action: PayloadAction<ClientAccident[]>) {
            state.accidents.items = action.payload
            state.accidents.loading = false
            state.accidents.error = null
        },
        setAccidentsError(state, action: PayloadAction<string>) {
            state.accidents.error = action.payload
            state.accidents.loading = false
        },

        // Drug Tests
        setDrugTestsLoading(state, action: PayloadAction<boolean>) {
            state.drugTests.loading = action.payload
        },
        setDrugTests(state, action: PayloadAction<ClientDrugTestRecord[]>) {
            state.drugTests.items = action.payload
            state.drugTests.loading = false
            state.drugTests.error = null
        },
        setDrugTestsError(state, action: PayloadAction<string>) {
            state.drugTests.error = action.payload
            state.drugTests.loading = false
        },

        // IFTA Reports
        setIFTAReportsLoading(state, action: PayloadAction<boolean>) {
            state.iftaReports.loading = action.payload
        },
        setIFTAReports(state, action: PayloadAction<ClientIFTARecord[]>) {
            state.iftaReports.items = action.payload
            state.iftaReports.loading = false
            state.iftaReports.error = null
        },
        setIFTAReportsError(state, action: PayloadAction<string>) {
            state.iftaReports.error = action.payload
            state.iftaReports.loading = false
        },
        removeIFTAReport(state, action: PayloadAction<string>) {
            state.iftaReports.items = state.iftaReports.items.filter((r) => r.id !== action.payload)
        },

        // Company Files
        setCompanyFilesLoading(state, action: PayloadAction<boolean>) {
            state.companyFiles.loading = action.payload
        },
        setCompanyFiles(state, action: PayloadAction<ClientCompanyFile[]>) {
            state.companyFiles.items = action.payload
            state.companyFiles.loading = false
            state.companyFiles.error = null
        },
        setCompanyFilesError(state, action: PayloadAction<string>) {
            state.companyFiles.error = action.payload
            state.companyFiles.loading = false
        },

        // Compliance Alerts
        setSafetyAlertsLoading(state, action: PayloadAction<boolean>) {
            state.complianceAlerts.loading = action.payload
        },
        setSafetyAlerts(state, action: PayloadAction<ComplianceAlert[]>) {
            state.complianceAlerts.data = action.payload
            state.complianceAlerts.loading = false
            state.complianceAlerts.error = null
        },
        setSafetyAlertsError(state, action: PayloadAction<string>) {
            state.complianceAlerts.error = action.payload
            state.complianceAlerts.loading = false
        },

        // DQF Checklist Config
        setDqfChecklistConfigLoading(state, action: PayloadAction<boolean>) {
            state.dqfChecklistConfig.loading = action.payload
        },
        setDqfChecklistConfig(state, action: PayloadAction<DqfChecklistItemConfig[]>) {
            state.dqfChecklistConfig.items = action.payload
            state.dqfChecklistConfig.loading = false
            state.dqfChecklistConfig.error = null
        },
        setDqfChecklistConfigError(state, action: PayloadAction<string>) {
            state.dqfChecklistConfig.error = action.payload
            state.dqfChecklistConfig.loading = false
        },
    },
})

export const {
    setSafetyDashboardLoading,
    setSafetyDashboardData,
    setSafetyDashboardError,
    setDriversLoading,
    setDrivers,
    setDriversError,
    setTrucksLoading,
    setTrucks,
    setTrucksError,
    setDOTInspectionsLoading,
    setDOTInspections,
    setDOTInspectionsError,
    updateDOTInspectionAttachments,
    setAccidentsLoading,
    setAccidents,
    setAccidentsError,
    setDrugTestsLoading,
    setDrugTests,
    setDrugTestsError,
    setIFTAReportsLoading,
    setIFTAReports,
    setIFTAReportsError,
    removeIFTAReport,
    setCompanyFilesLoading,
    setCompanyFiles,
    setCompanyFilesError,
    setSafetyAlertsLoading,
    setSafetyAlerts,
    setSafetyAlertsError,
    setDqfChecklistConfigLoading,
    setDqfChecklistConfig,
} = safetySlice.actions

// Selectors
export const selectSafetyDashboard = (state: RootState) => state.safety.dashboard
export const selectSafetyDrivers = (state: RootState) => state.safety.drivers
export const selectSafetyTrucks = (state: RootState) => state.safety.trucks
export const selectDOTInspections = (state: RootState) => state.safety.dotInspections
export const selectAccidents = (state: RootState) => state.safety.accidents
export const selectDrugTests = (state: RootState) => state.safety.drugTests
export const selectIFTAReports = (state: RootState) => state.safety.iftaReports
export const selectCompanyFiles = (state: RootState) => state.safety.companyFiles
export const selectSafetyComplianceAlerts = (state: RootState) => state.safety.complianceAlerts
export const selectDqfChecklistConfig = (state: RootState) => state.safety.dqfChecklistConfig

export default safetySlice.reducer
